// Generic confirmation modal for user actions on the admin Users
// page. Used by force-password-change, suspend, and unsuspend so each
// row action has the same shape and a consistent dismiss UX.

import { X } from "lucide-react";
import { useTranslation } from "../i18n";
import { useReadOnly } from "../access/ReadOnly";
import "../styles/add-user.css";

interface Props {
    titleKey: string;
    bodyKey: string;
    bodyParams?: Record<string, string | number>;
    confirmLabelKey: string;
    confirmTone?: "danger" | "primary";
    onConfirm: () => void;
    onCancel: () => void;
    submitting?: boolean;
}

const UserActionConfirm = ({
    titleKey,
    bodyKey,
    bodyParams,
    confirmLabelKey,
    confirmTone = "danger",
    onConfirm,
    onCancel,
    submitting = false,
}: Props) => {
    const { t } = useTranslation();
    // Access-control read-only handheld mode (ACCESS_CONTROL_PLAN.md §8.4).
    const readOnly = useReadOnly();
    const confirmClass = confirmTone === "danger" ? "btn btn-danger" : "btn btn-primary";

    return (
        <div className="modal-overlay" onClick={onCancel}>
            <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                <div className="modal-card-header">
                    <h2>{t(titleKey)}</h2>
                    <button className="modal-close" onClick={onCancel} disabled={submitting}>
                        <X size={18} />
                    </button>
                </div>
                <div className="modal-card-body">
                    <p className="modal-confirm-text">{t(bodyKey, bodyParams)}</p>
                    <div className="modal-confirm-buttons">
                        <button
                            className={confirmClass}
                            onClick={onConfirm}
                            disabled={submitting || readOnly}
                            aria-disabled={readOnly}
                            title={readOnly ? t("read_only_mode") : undefined}
                        >
                            {submitting ? t("submitting") : t(confirmLabelKey)}
                        </button>
                        <button
                            className="btn btn-ghost"
                            onClick={onCancel}
                            disabled={submitting}
                        >
                            {t("cancel")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserActionConfirm;
